interface SchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  format?: string;
  items?: any;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  enum?: (string | number)[];
  enumNames?: string[];
  default?: boolean | string | number | (string | number)[];
  uniqueItems?: boolean;
  properties?: { [key: string]: SchemaProperty };
  required?: string[];
  $ref?: string;
  [key: string]: any;
}

interface Schema {
  type?: string;
  title?: string;
  description?: string;
  properties?: { [key: string]: SchemaProperty };
  required?: string[];
  definitions?: { [key: string]: SchemaProperty };
  [key: string]: any;
}

interface UiSchema {
  'ui:order'?: string[];
  'ui:description'?: string;
  'ui:enableMarkdownInDescription'?: boolean;
  'ui:widget'?: string;
  'ui:options'?: any;
  'ui:field'?: string;
  [key: string]: any;
}

interface FormSchema {
  schema: Schema;
  uiSchema: UiSchema;
  [key: string]: any;
}

function cleanupUiSchemaRecursive(
  properties: { [key: string]: SchemaProperty } | undefined,
  uiSchema: UiSchema,
): void {
  if (!properties || !uiSchema) {
    return;
  }

  const validKeys = Object.keys(properties);
  const currentUiKeys = Object.keys(uiSchema);

  for (const uiKey of currentUiKeys) {
    if (!uiKey.startsWith('ui:') && !validKeys.includes(uiKey)) {
      delete uiSchema[uiKey];
    }
  }

  if (uiSchema['ui:order']) {
    uiSchema['ui:order'] = uiSchema['ui:order'].filter(key => validKeys.includes(key));
    if (uiSchema['ui:order'].length === 0) {
      const hasOtherUiDirectives = Object.keys(uiSchema).some(
        k => k.startsWith('ui:') && k !== 'ui:order',
      );
      if (!hasOtherUiDirectives && Object.keys(uiSchema).length === 1) {
        delete uiSchema['ui:order'];
      } else if (uiSchema['ui:order'].length === 0) {
        delete uiSchema['ui:order'];
      }
    }
  }

  for (const propKey of validKeys) {
    const property = properties[propKey];
    const nestedUiSchema = uiSchema[propKey];

    if (
      property.type === 'object' &&
      property.properties &&
      nestedUiSchema &&
      typeof nestedUiSchema === 'object' &&
      !Array.isArray(nestedUiSchema)
    ) {
      cleanupUiSchemaRecursive(property.properties, nestedUiSchema as UiSchema);
      if (Object.keys(nestedUiSchema).length === 0) {
        delete uiSchema[propKey];
      }
    }
  }
}

export function convertToCompatibleSchema(inputSchema: FormSchema): FormSchema {
  const schema: FormSchema = JSON.parse(JSON.stringify(inputSchema));

  if (!schema.schema) {
    schema.schema = { type: 'object', properties: {} };
  }
  if (!schema.schema.type) {
    schema.schema.type = 'object';
  }
  if (!schema.schema.properties) {
    schema.schema.properties = {};
  }
  if (!schema.uiSchema) {
    schema.uiSchema = {};
  }

  if (schema.schema.description) {
    schema.uiSchema['ui:description'] = schema.schema.description;
  }

  const definitions = schema.schema.definitions || {};

  if (schema.schema.properties) {
    processProperties(schema.schema.properties, schema.uiSchema, definitions);
  }

  cleanupUiSchemaRecursive(schema.schema.properties, schema.uiSchema);

  const finalSchemaProperties = schema.schema.properties || {};
  const validTopLevelKeys = Object.keys(finalSchemaProperties);

  if (!schema.uiSchema['ui:order'] && validTopLevelKeys.length > 0) {
    schema.uiSchema['ui:order'] = validTopLevelKeys;
  } else if (schema.uiSchema['ui:order']) {
    schema.uiSchema['ui:order'] = schema.uiSchema['ui:order'].filter(key =>
      validTopLevelKeys.includes(key),
    );
    if (schema.uiSchema['ui:order'].length === 0) {
      delete schema.uiSchema['ui:order'];
    }
  }

  if (schema.schema.definitions) {
    delete schema.schema.definitions;
  }

  if (schema.uiSchema['ui:description'] && schema.schema.description) {
    delete schema.schema.description;
  }

  return schema;
}

function processProperties(
  properties: { [key: string]: SchemaProperty },
  uiSchema: UiSchema,
  definitions: { [key: string]: SchemaProperty },
): void {
  for (const propKey in properties) {
    if (!properties.hasOwnProperty(propKey)) {
      continue;
    }
    const property = properties[propKey];

    if (property.$ref) {
      const refPath = property.$ref.replace('#/definitions/', '');
      if (definitions[refPath]) {
        const originalTitle = property.title;
        const originalDesc = property.description;
        const originalWidget = uiSchema[propKey]?.['ui:widget'];
        const originalOptions = uiSchema[propKey]?.['ui:options'];

        const definitionCopy = JSON.parse(JSON.stringify(definitions[refPath]));

        for (const key in property) {
          if (key !== 'title' && key !== 'description') {
            delete property[key];
          }
        }

        for (const key in definitionCopy) {
          if (key !== 'title' && key !== 'description') {
            property[key] = definitionCopy[key];
          }
        }

        if (originalTitle) {
          property.title = originalTitle;
        } else if (definitionCopy.title) {
          property.title = definitionCopy.title;
        }

        if (originalDesc) {
          property.description = originalDesc;
        } else if (definitionCopy.description) {
          property.description = definitionCopy.description;
        }

        delete property.$ref;

        if (originalWidget || originalOptions) {
          if (!uiSchema[propKey]) uiSchema[propKey] = {};
          if (originalWidget) uiSchema[propKey]['ui:widget'] = originalWidget;
          if (originalOptions) uiSchema[propKey]['ui:options'] = originalOptions;
        }
      } else {
        console.warn(
          `Could not resolve $ref: ${property.$ref} for property ${propKey}. Removing property.`,
        );
        delete properties[propKey];
        delete uiSchema[propKey];
        continue;
      }
    }

    if (!property.title) {
      property.title = ' ';
    }

    if (property.type === 'integer') {
      property.type = 'number';
    }

    if (!uiSchema[propKey]) {
      uiSchema[propKey] = {};
    }

    uiSchema[propKey]['ui:enableMarkdownInDescription'] = true;

    if (property.description) {
      uiSchema[propKey]['ui:description'] = property.description;
      delete property.description;
    } else if (!uiSchema[propKey]['ui:description']) {
      uiSchema[propKey]['ui:description'] = '';
    }

    delete uiSchema[propKey]['ui:field'];

    if (property.type === 'object' && property.properties) {
      if (typeof uiSchema[propKey] !== 'object' || Array.isArray(uiSchema[propKey])) {
        uiSchema[propKey] = {};
      }
      processProperties(property.properties, uiSchema[propKey] as UiSchema, definitions);
    }

    if (
      property.type === 'array' &&
      property.items &&
      typeof property.items === 'object' &&
      !Array.isArray(property.items) &&
      property.items.properties
    ) {
      if (!uiSchema[propKey]) uiSchema[propKey] = {};
      if (!uiSchema[propKey].items) uiSchema[propKey].items = {};

      if (typeof uiSchema[propKey].items === 'object' && !Array.isArray(uiSchema[propKey].items)) {
        processProperties(
          property.items.properties,
          uiSchema[propKey].items as UiSchema,
          definitions,
        );
      }
    }
  }
}
